import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import { ChevronDownIcon, XCircleIcon } from '@heroicons/react/16/solid'

interface SimpleComboBoxProps {
  items: any[]
  selected: any
  setSelected: (selected: any) => void
  getOptionLabel: (item: any) => React.ReactNode
  getSelectedLabel: (item: any) => React.ReactNode
  getValue: (item: any) => any
  clearable?: boolean
  placeholder?: string
  status?: 'success' | 'error'
  disabled?: boolean
  searchable?: boolean
  align?: 'center' | 'end' | 'start'
  empty?: React.ReactNode
  fixedOption?: React.ReactNode
  commandListClassName?: string
  getFixedOptionLabel?: () => React.ReactNode
}

export default function SimpleComboBox({
  items = [],
  selected,
  setSelected,
  getOptionLabel,
  getSelectedLabel,
  getValue,
  clearable = false,
  placeholder = 'Select...',
  status: _status,
  disabled,
  searchable = true,
  align = 'start',
  empty = 'No items available',
  fixedOption,
  commandListClassName,
  getFixedOptionLabel,
}: SimpleComboBoxProps) {
  const [open, setOpen] = React.useState(false)
  //   const [value, setValue] = React.useState('')
  const selectedValue = selected ? getValue(selected) : null
  const status = disabled ? 'disabled' : _status
  const [fixedOptionSelected, setFixedOptionSelected] = React.useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-slot="control"
          disabled={disabled}
          data-status={status}
          data-focusable="true"
          variant="ghost"
          size="lg"
          role="combobox"
          aria-expanded={open}
          className={[
            'w-full justify-between border-0 text-sm shadow-input-default hover:border-secondary hover:bg-transparent data-[state=open]:shadow-input-focus',
            'placeholder:text-txt-placeholder focus-within:!shadow-input-focus hover:shadow-input-hover active:shadow-input-focus disabled:cursor-not-allowed data-[status=disabled]:pointer-events-none data-[status=disabled]:bg-gray-tr-08 data-[status=disabled]:text-txt-disabled',
            'data-[status=error]:shadow-input-invalid data-[status=success]:shadow-input-success data-[status=error]:focus-within:!shadow-input-invalid data-[status=success]:focus-within:!shadow-input-success data-[status=error]:hover:shadow-input-invalid data-[status=success]:hover:shadow-input-success',
          ].join(' ')}
        >
          <span className="w-[90%] overflow-hidden text-ellipsis text-left font-normal">
            {selected ? (
              getSelectedLabel(selected)
            ) : fixedOption && fixedOptionSelected && getFixedOptionLabel ? (
              getFixedOptionLabel()
            ) : (
              <p className="text-txt-placeholder">{placeholder}</p>
            )}
          </span>
          <div className="flex items-center gap-1">
            {clearable && selected && (
              <Button
                iconOnly
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault()
                  setSelected(null)
                  setFixedOptionSelected(false)
                }}
              >
                <XCircleIcon />
              </Button>
            )}

            <ChevronDownIcon />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto relative w-full max-w-[90vw] overflow-y-auto p-0 md:max-w-[60vw]"
        align={align}
      >
        <Command>
          {searchable && (
            <CommandInput
              className="text-txt-placeholder"
              placeholder={placeholder}
            />
          )}
          {fixedOption && (
            <div
              onClick={() => {
                setOpen(false)
                setFixedOptionSelected(true)
              }}
              className="w-[280px] cursor-default  border-b px-1  text-sm text-foreground "
            >
              <div className="rounded px-3 py-2 hover:bg-gray-tr-08 [&_[data-slot=description]]:text-txt-secondary [&_[data-slot=description]]:data-[selecteditem]:text-txt-accent">
                {fixedOption}
              </div>
            </div>
          )}
          <CommandList
            className={`${commandListClassName ? commandListClassName : 'w-[280px]'}`}
          >
            <CommandEmpty className="p-3 text-left text-sm font-normal text-txt-placeholder">
              {empty}
            </CommandEmpty>
            <CommandGroup className=" max-h-48 overflow-y-auto">
              {items.map((item) => {
                const currentValue = getValue(item)
                return (
                  <CommandItem
                    key={currentValue}
                    value={currentValue}
                    onSelect={(currentValue) => {
                      const val = currentValue
                      setSelected(val === currentValue ? item : null)
                      setOpen(false)
                    }}
                    data-selecteditem={
                      currentValue === selectedValue ? true : undefined
                    }
                    className="rounded px-3 hover:bg-gray-tr-08 data-[selecteditem]:bg-accent-tr-08 data-[selecteditem]:text-txt-accent  data-[selecteditem]:hover:bg-accent-tr-12 [&_[data-slot=description]]:text-txt-secondary [&_[data-slot=description]]:data-[selecteditem]:text-txt-accent"
                  >
                    {getOptionLabel(item)}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

SimpleComboBox.isFormControl = true
